export enum CustomInstruments {
  TargetLesion = 'TargetLesion',
  NonTargetLesion = 'NonTargetLesion',
  KneeMalAlignment = 'KneeMalAlignment',
  SpinalFusionAssessment = 'SpinalFusionAssessment',
  DynamikaEllipticalROI = 'DynamikaEllipticalROI',
  Ruler = 'DynamikaLength',
}

// Relocated from extensions/dynamika-services/.../LesionService/types.ts when the
// LesionService was removed (D7-7208). Kept here so the measurement-table panel and
// any tool classification keep a single canonical enum.
export enum LesionToolNamesEnum {
  TargetLesion = 'TargetLesion',
  NonTargetLesion = 'NonTargetLesion',
  SpinalFusionAssessment = 'SpinalFusionAssessment',
}

// Single canonical instrument-code -> cornerstone tool-name transform (D7-7208).
// Consumed by the activation path (measurements.saga.ts `activateToolWorker`) and
// the inventory-resolution path. Replaces the local `toolsList` that used to live
// in the saga. Mirror of the former `toolsList`, incl. the D7-6906 additions
// (`DYNAMIKA_CIRCLE_ROI`, `DYNAMIKA_LENGTH`, `LENGTH`).
// NOTE: `SEGMENTATION_INSTRUMENT` is intentionally absent — segmentation is armed
// through the segmentation workers (ByConfig/ByLabel), never through
// `activateToolWorker`, so it needs no cornerstone tool-name mapping here.
export const INSTRUMENT_CODE_TO_TOOL = {
  MALALAIGNMENT_ANGLE_INSTRUMENT: 'KneeMalAlignment',
  CROSS_RULER: 'TargetLesion',
  DYNAMIKA_CROSS_RULER: 'TargetLesion',
  // D7-7780: RULER is a form instrument, so it must resolve to the form-routable
  // clone. Native 'Length' sits outside FORM_ROUTABLE_TOOLS, so armInstrument
  // silently refuses it and the routing gates then drop whatever is drawn.
  RULER: 'DynamikaLength',
  MARKER: 'NonTargetLesion',
  DYNAMIKA_MARKER: 'NonTargetLesion',
  SPINAL_FUSION_ASSESSMENT_INSTRUMENT: 'SpinalFusionAssessment',
  DYNAMIKA_ELLIPTICAL_ROI: 'DynamikaEllipticalROI',
  DYNAMIKA_CIRCLE_ROI: 'DynamikaCircleROI',
  DYNAMIKA_LENGTH: 'DynamikaLength',
  LENGTH: 'DynamikaLength',
} as const;

// Tools eligible for form routing (D7-7208). Native `Length` stays out so free-hand
// draws never route (D7-4732). All toolbar-removed except TOOLBAR_VISIBLE_INSTRUMENTS.
export const FORM_ROUTABLE_TOOLS = new Set<string>([
  'TargetLesion',
  'NonTargetLesion',
  'KneeMalAlignment',
  'SpinalFusionAssessment',
  'DynamikaEllipticalROI',
  'DynamikaCircleROI',
  'DynamikaLength',
]);

// D7-7999: custom instruments temporarily back in the toolbar, as before 7.19.0.
export const TOOLBAR_VISIBLE_INSTRUMENTS: readonly string[] = [
  'KneeMalAlignment',
  'SpinalFusionAssessment',
];

// Eligible for form routing (D7-7208) — NOT provenance: since D7-7999 a toolbar draw
// of a visible instrument also returns true, unarmed and without instrument config.
export const isFormRoutable = (toolName?: string): boolean =>
  !!toolName && FORM_ROUTABLE_TOOLS.has(toolName);

/**
 * Resolve a clinical-trial inventory instrument code to its cornerstone tool name.
 * Returns `undefined` for unknown codes.
 */
export const resolveToolName = (instrumentCode?: string): string | undefined =>
  instrumentCode ? (INSTRUMENT_CODE_TO_TOOL as Record<string, string>)[instrumentCode] : undefined;
